// Chart utilities for History page visualization

import { TARGET_RANGES } from './constants'

// Parameter configuration for charts
export const CHART_PARAMETERS = {
  free_chlorine: {
    key: 'free_chlorine',
    label: 'Free Chlorine',
    shortLabel: 'FC',
    color: '#4CAF50',
    unit: 'ppm',
    yAxisId: 'chlorine',
    strokeWidth: 2,
    dot: { fill: '#4CAF50', strokeWidth: 0, r: 3 }
  },
  combined_chlorine: {
    key: 'combined_chlorine',
    label: 'Combined Chlorine',
    shortLabel: 'CC',
    color: '#FF9800',
    unit: 'ppm',
    yAxisId: 'chlorine',
    strokeWidth: 2,
    dot: { fill: '#FF9800', strokeWidth: 0, r: 3 }
  },
  ph: {
    key: 'ph',
    label: 'pH',
    shortLabel: 'pH',
    color: '#2196F3',
    unit: '',
    yAxisId: 'ph',
    strokeWidth: 3,
    dot: { fill: '#2196F3', strokeWidth: 0, r: 4 }
  },
  total_alkalinity: {
    key: 'total_alkalinity',
    label: 'Total Alkalinity',
    shortLabel: 'TA',
    color: '#9C27B0',
    unit: 'ppm',
    yAxisId: 'high',
    strokeWidth: 2,
    dot: { fill: '#9C27B0', strokeWidth: 0, r: 3 }
  },
  cya_stabilizer: {
    key: 'cya_stabilizer',
    label: 'CYA/Stabilizer',
    shortLabel: 'CYA',
    color: '#795548',
    unit: 'ppm',
    yAxisId: 'cya',
    strokeWidth: 2,
    dot: { fill: '#795548', strokeWidth: 0, r: 3 }
  },
  calcium_hardness: {
    key: 'calcium_hardness',
    label: 'Calcium Hardness',
    shortLabel: 'CH',
    color: '#009688',
    unit: 'ppm',
    yAxisId: 'high',
    strokeWidth: 2,
    dot: { fill: '#009688', strokeWidth: 0, r: 3 }
  },
  water_temperature: {
    key: 'water_temperature',
    label: 'Water Temperature',
    shortLabel: 'Temp',
    color: '#F44336',
    unit: '°F',
    yAxisId: 'temp',
    strokeWidth: 2,
    dot: { fill: '#F44336', strokeWidth: 0, r: 3 }
  }
}

// Y-axis configuration
export const Y_AXIS_CONFIG = {
  ph: {
    domain: [6.8, 8.2],
    label: 'pH',
    orientation: 'left',
    color: '#2196F3'
  },
  chlorine: {
    domain: [0, 'dataMax + 1'],
    label: 'Chlorine (ppm)',
    orientation: 'right',
    color: '#4CAF50'
  },
  high: {
    domain: [0, 'dataMax + 50'],
    label: 'TA, CH (ppm)',
    orientation: 'right',
    color: '#9C27B0'
  },
  cya: {
    domain: [0, 'dataMax + 10'],
    label: 'CYA (ppm)',
    orientation: 'right',
    color: '#795548'
  },
  temp: {
    domain: [60, 110],
    label: 'Temperature (°F)',
    orientation: 'right',
    color: '#F44336'
  }
}

// Time range presets
export const TIME_RANGES = {
  '1W': { label: '1 Week', days: 7 },
  '1M': { label: '1 Month', days: 30 },
  '3M': { label: '3 Months', days: 90 },
  '6M': { label: '6 Months', days: 180 },
  '1Y': { label: '1 Year', days: 365 },
  'ALL': { label: 'All Time', days: null }
}

// Chemical addition categories and colors
export const CHEMICAL_CATEGORIES = {
  sanitizer: { color: '#4CAF50', label: 'Sanitizers' },
  ph_adjuster: { color: '#2196F3', label: 'pH Adjusters' },
  alkalinity: { color: '#9C27B0', label: 'Alkalinity' },
  cya: { color: '#795548', label: 'CYA/Stabilizer' },
  calcium: { color: '#009688', label: 'Calcium' },
  other: { color: '#9E9E9E', label: 'Other' }
}

// Chemical type to letter mapping for chart icons
export const CHEMICAL_ICONS = {
  // Sanitizers (Chlorine-based)
  'liquid_chlorine': 'L',
  'liquid_chlorine_10': 'L',
  'liquid_chlorine_12': 'L', 
  'liquid_chlorine_12_5': 'L',
  'household_bleach': 'H',
  'trichlor': 'T',
  'dichlor': 'D',
  'cal_hypo_48': 'C',
  'cal_hypo_53': 'C',
  'cal_hypo_65': 'C',
  'cal_hypo_70': 'C',
  'cal_hypo_73': 'C',
  'cal_hypo_78': 'C',
  'lithium_hypo': 'X',
  'chlorine_gas': 'G',
  'swg': 'S',
  
  // pH Adjusters
  'muriatic_acid': 'M',
  'soda_ash': 'A',
  'borax': 'B',
  
  // Alkalinity
  'baking_soda': 'K',
  
  // CYA/Stabilizer  
  'dry_stabilizer': 'Y',
  'liquid_stabilizer': 'Y',
  
  // Calcium
  'calcium_chloride': 'R',
  'calcium_hardness_increaser': 'R'
}

// Get chemical icon letter from chemical name
export const getChemicalIcon = (chemicalName) => {
  if (!chemicalName) return '?'
  
  // Convert to lowercase and replace spaces/special chars with underscores
  const normalizedName = chemicalName.toLowerCase()
    .replace(/[\s\-/()%.]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
  
  // First try exact match
  if (CHEMICAL_ICONS[normalizedName]) {
    return CHEMICAL_ICONS[normalizedName]
  }
  
  // Try partial matches for common variations
  const lowerName = chemicalName.toLowerCase()
  
  // Liquid chlorine variations
  if (lowerName.includes('liquid chlorine') || lowerName.includes('liquid_chlorine')) {
    return 'L'
  }
  
  // Muriatic acid variations  
  if (lowerName.includes('muriatic')) {
    return 'M'
  }
  
  // Trichlor variations
  if (lowerName.includes('trichlor') || lowerName.includes('tri-chlor')) {
    return 'T'
  }
  
  // Dichlor variations
  if (lowerName.includes('dichlor') || lowerName.includes('di-chlor')) {
    return 'D'
  }
  
  // Cal-hypo variations
  if (lowerName.includes('cal') && (lowerName.includes('hypo') || lowerName.includes('hypochlorite'))) {
    return 'C'
  }
  
  // Soda ash variations
  if (lowerName.includes('soda ash') || lowerName.includes('washing soda')) {
    return 'A'
  }
  
  // Baking soda variations
  if (lowerName.includes('baking soda') || lowerName.includes('sodium bicarbonate')) {
    return 'K'
  }
  
  // CYA variations
  if (lowerName.includes('stabilizer') || lowerName.includes('cya') || lowerName.includes('cyanuric')) {
    return 'Y'
  }
  
  // Calcium variations
  if (lowerName.includes('calcium')) {
    return 'R'
  }
  
  // Borax variations
  if (lowerName.includes('borax')) {
    return 'B'
  }
  
  // Bleach variations
  if (lowerName.includes('bleach')) {
    return 'H'
  }
  
  // Default to first character if no match found
  return chemicalName.charAt(0).toUpperCase()
}

/**
 * Transform measurements data for chart consumption
 * @param {Array} measurements - Raw measurements from database
 * @param {Date} startDate - Filter start date
 * @param {Date} endDate - Filter end date
 * @returns {Array} Chart-ready data
 */
export const prepareChartData = (measurements, startDate = null, endDate = null) => {
  if (!measurements || measurements.length === 0) return []
  
  // Filter by date range if provided
  let filteredMeasurements = measurements
  if (startDate || endDate) {
    filteredMeasurements = measurements.filter(measurement => {
      const measuredAt = new Date(measurement.measured_at)
      if (startDate && measuredAt < startDate) return false
      if (endDate && measuredAt > endDate) return false
      return true
    })
  }
  
  // Sort by date (oldest first for chart)
  const sortedMeasurements = [...filteredMeasurements].sort((a, b) => 
    new Date(a.measured_at) - new Date(b.measured_at)
  )
  
  // Transform for chart
  return sortedMeasurements.map(measurement => ({
    date: new Date(measurement.measured_at).getTime(),
    dateString: new Date(measurement.measured_at).toLocaleDateString(),
    fullDate: measurement.measured_at,
    free_chlorine: measurement.free_chlorine,
    combined_chlorine: measurement.combined_chlorine,
    ph: measurement.ph,
    total_alkalinity: measurement.total_alkalinity,
    cya_stabilizer: measurement.cya_stabilizer,
    calcium_hardness: measurement.calcium_hardness,
    water_temperature: measurement.water_temperature,
    id: measurement.id,
    notes: measurement.notes
  }))
}

/**
 * Get chemical additions within a date range
 * @param {Array} chemicalAdditions - Raw chemical additions from database
 * @param {Date} startDate - Filter start date
 * @param {Date} endDate - Filter end date
 * @returns {Array} Filtered chemical additions
 */
export const getChemicalAdditionsInRange = (chemicalAdditions, startDate = null, endDate = null) => {
  if (!chemicalAdditions || chemicalAdditions.length === 0) return []
  
  return chemicalAdditions.filter(addition => {
    const addedAt = new Date(addition.added_at)
    if (startDate && addedAt < startDate) return false
    if (endDate && addedAt > endDate) return false
    return true
  }).map(addition => ({
    ...addition,
    timestamp: new Date(addition.added_at).getTime(),
    category: addition.chemical_types?.category || 'other',
    displayName: addition.chemical_types?.name || 'Unknown Chemical'
  }))
}

/**
 * Calculate date ranges for time range selector
 * @param {string} range - Range key ('1W', '1M', etc.)
 * @param {Date} customStart - Custom start date
 * @param {Date} customEnd - Custom end date
 * @returns {Object} Start and end dates
 */
export const calculateDateRange = (range, customStart = null, customEnd = null) => {
  const now = new Date()
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  
  if (range === 'CUSTOM' && customStart && customEnd) {
    return {
      startDate: new Date(customStart),
      endDate: new Date(customEnd)
    }
  }
  
  if (range === 'ALL') {
    return {
      startDate: null,
      endDate: null
    }
  }
  
  const timeRange = TIME_RANGES[range]
  if (!timeRange || !timeRange.days) {
    return { startDate: null, endDate: null }
  }
  
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - timeRange.days)
  startDate.setHours(0, 0, 0, 0)
  
  return { startDate, endDate }
}

/**
 * Get target range bands for chart visualization
 * @param {Object} poolConfig - Pool configuration
 * @param {Object} currentMeasurement - Latest measurement for CYA-dependent calculations
 * @returns {Object} Target ranges for chart bands
 */
export const getTargetRangeBands = (poolConfig, currentMeasurement = null) => {
  const bands = {}
  
  // pH range (static)
  bands.ph = {
    min: TARGET_RANGES.PH.min,
    max: TARGET_RANGES.PH.max,
    ideal: TARGET_RANGES.PH.ideal
  }
  
  // Free chlorine range (CYA-dependent)
  const cyaLevel = currentMeasurement?.cya_stabilizer || TARGET_RANGES.CYA_STABILIZER.ideal
  bands.free_chlorine = {
    min: Math.max(cyaLevel * TARGET_RANGES.FREE_CHLORINE.min_multiplier, TARGET_RANGES.FREE_CHLORINE.absolute_min),
    max: Math.min(cyaLevel * TARGET_RANGES.FREE_CHLORINE.max_multiplier, TARGET_RANGES.FREE_CHLORINE.absolute_max),
    ideal: cyaLevel * 0.075 // 7.5% of CYA
  }
  
  // Combined chlorine range
  bands.combined_chlorine = TARGET_RANGES.COMBINED_CHLORINE
  
  // Total alkalinity range
  bands.total_alkalinity = TARGET_RANGES.TOTAL_ALKALINITY
  
  // CYA range
  bands.cya_stabilizer = TARGET_RANGES.CYA_STABILIZER
  
  // Calcium hardness range (pool type dependent)
  const poolType = poolConfig?.pool_type || 'vinyl_liner'
  bands.calcium_hardness = TARGET_RANGES.CALCIUM_HARDNESS[poolType] || TARGET_RANGES.CALCIUM_HARDNESS.vinyl_liner
  
  // Water temperature range
  bands.water_temperature = TARGET_RANGES.WATER_TEMPERATURE
  
  return bands
}

/**
 * Format value for chart tooltip
 * @param {number} value - Value to format
 * @param {string} parameter - Parameter key
 * @returns {string} Formatted value
 */
export const formatChartValue = (value, parameter) => {
  if (value === null || value === undefined) return 'N/A'
  
  const config = CHART_PARAMETERS[parameter]
  if (!config) return value.toString()
  
  if (parameter === 'ph') {
    return value.toFixed(1)
  } else if (config.unit === 'ppm' || config.unit === '°F') {
    return `${value.toFixed(1)} ${config.unit}`
  }
  
  return value.toString()
}

/**
 * Get visible parameters based on screen size and user preferences
 * @param {boolean} isMobile - Is mobile device
 * @param {Array} hiddenParameters - User-hidden parameters
 * @returns {Array} Visible parameter keys
 */
export const getVisibleParameters = (isMobile = false, hiddenParameters = []) => {
  const allParameters = Object.keys(CHART_PARAMETERS)
  
  if (isMobile) {
    // Show fewer parameters on mobile by default
    const mobileDefaults = ['free_chlorine', 'ph', 'total_alkalinity']
    return mobileDefaults.filter(param => !hiddenParameters.includes(param))
  }
  
  return allParameters.filter(param => !hiddenParameters.includes(param))
}

/**
 * Calculate smart chart domain for y-axes
 * @param {Array} data - Chart data
 * @param {string} yAxisId - Y-axis identifier
 * @returns {Array} Domain [min, max]
 */
export const calculateChartDomain = (data, yAxisId) => {
  if (!data || data.length === 0) return [0, 10]
  
  // Get all parameters using this y-axis
  const parameters = Object.values(CHART_PARAMETERS).filter(p => p.yAxisId === yAxisId)
  
  // Collect all values for these parameters
  const values = []
  data.forEach(point => {
    parameters.forEach(param => {
      const value = point[param.key]
      if (value !== null && value !== undefined) {
        values.push(value)
      }
    })
  })
  
  if (values.length === 0) return [0, 10]
  
  const min = Math.min(...values)
  const max = Math.max(...values)
  const padding = (max - min) * 0.1 // 10% padding
  
  // Apply axis-specific logic
  if (yAxisId === 'ph') {
    return [Math.max(6.5, min - padding), Math.min(8.5, max + padding)]
  } else if (yAxisId === 'chlorine') {
    return [0, Math.max(10, max + padding)]
  } else if (yAxisId === 'high') {
    return [0, Math.max(100, max + padding)]
  } else if (yAxisId === 'cya') {
    return [0, Math.max(50, max + padding)]
  } else if (yAxisId === 'temp') {
    return [Math.max(50, min - padding), Math.min(120, max + padding)]
  }
  
  return [Math.max(0, min - padding), max + padding]
}